/**
 * Сторно на редове от отворена сметка (Маркиране) — ЕДНА заявка за всички
 * редове, избрани заедно в „Сторно" (`POST /bills/{id}/cancel-items`, BE 0.0.274,
 * FiscalAPI 1.94.0 за локалния режим).
 *
 * Преди това прозорецът пращаше по една `/cancel-item` заявка на ред и всяка
 * печаташе СВОЙ кухненски билет: 8 сторнирани продукта = 8 листа от принтера.
 * Сега сървърът сторнира всичко в една транзакция (всичко или нищо) и печата
 * ЕДИН билет на станция.
 *
 * Сървър, който още не знае адреса, се разпознава по категоричния отговор и
 * тогава се пада на стария път ред по ред (пак 8 билета, но нищо не се губи):
 * облак < 0.0.274 → 404 (405); каса < 1.94.0 → 503 „Недостъпно без интернет"
 * от огледалото (всеки непознат POST там отговаря така). 503 с друг текст
 * (заета база) и мрежова грешка/таймаут НЕ са липсващ адрес — сторното може
 * вече да е минало, затова не се повтаря по друг път.
 */
import axios from "axios";
import { api } from "./client";
import { cancelBillItem, type BillOut } from "./bills";

export interface CancelBillItemLineIn {
  billElementId: number;
  billElementItemId: number;
  /** Липсващо = целият ред. */
  quantity?: number;
}

export interface CancelBillItemsIn {
  items: CancelBillItemLineIn[];
  reason?: string;
  isDefective?: boolean;
}

/** Текстът на касата за непознат маршрут (FiscalAPI `app/edge/__init__.py`). */
const TILL_UNKNOWN_ROUTE_DETAIL = "Недостъпно без интернет";

/** Сървърът категорично НЯМА `cancel-items` — единственият случай за стария път. */
export function isCancelItemsRouteMissing(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) return false;
  const { status, data } = error.response;
  if (status === 404 || status === 405) return true;
  const detail = (data as { detail?: unknown } | undefined)?.detail;
  return status === 503 && detail === TILL_UNKNOWN_ROUTE_DETAIL;
}

/** Старият път: по една `/cancel-item` на ред, спира на първата грешка. */
async function cancelLineByLine(
  billId: number,
  data: CancelBillItemsIn,
): Promise<BillOut> {
  let bill: BillOut | undefined;
  for (const line of data.items) {
    bill = await cancelBillItem(billId, {
      ...line,
      reason: data.reason,
      isDefective: data.isDefective,
    });
  }
  if (!bill) throw new Error("cancelBillItems: празен списък");
  return bill;
}

export const cancelBillItems = async (
  billId: number,
  data: CancelBillItemsIn,
): Promise<BillOut> => {
  try {
    const response = await api.post<BillOut>(
      `/bills/${billId}/cancel-items`,
      data,
    );
    return response.data;
  } catch (error: unknown) {
    if (isCancelItemsRouteMissing(error)) {
      return cancelLineByLine(billId, data);
    }
    throw error;
  }
};
